import { computed } from 'vue'
import { defineStore } from 'pinia'
import { getUrlBaseV2, getUrlApiV1 } from '@/utils/index'
import axios from 'axios'

export const useCRM = defineStore('crm', {
    state: () => ({
        funnels: [],
        currentFunnel: null,
        stages: {},
        negotiations: [],
        currentPage: 1,
        lastPage: 1,
        loadingMore: false,
        loading: {
            funnels: false,
            stages: false,
            negotiations: false,
            funnel: false,
            stage: false,
            negotiation: false,
        },
        filters: {

            sales_funnel_stage_id: null,
            status: null,
            client_id: null,
            user_id: null,
            min_value: null,
            max_value: null,
            created_since: null,
            created_until: null,
            won_since: null,
            won_until: null,
            lost_since: null,
            lost_until: null,
        },
        statuses: null,
        statusesPromise: null,
        lostReasons: null,
        lostReasonsPromise: null,
        products: null,
        productsPromise: null,
        selectedNegotiationId: null,
    }),
    getters: {
        getFunnels: (state) => state.funnels,
        getStages: (state) => state.stages,
        getNegotiations: (state) => state.negotiations,
        getNegotiationsByStage: (state) => {
            const grouped = {};
            state.negotiations.forEach(neg => {
                const stageId = neg.sales_funnel_stage_id;
                if (!grouped[stageId]) {
                    grouped[stageId] = [];
                }
                grouped[stageId].push(neg);
            });
            return grouped;
        },
        getSelectedNegotiation: (state) => {
            if (!state.selectedNegotiationId) return null;
            return state.negotiations.find(n => n && n.id == state.selectedNegotiationId) || null;
        }
    },
    actions: {
        fetchFunnel(id) {

            const base = getUrlBaseV2()
            axios.get(`${base}/sales/sales-funnels/${id}`)
                .then(res => {
                    const response = res.data
                    this.currentFunnel = response.data
                })
        },
        async fetchNegotiation(id) {
            this.loading.negotiation = true
            const base = getUrlBaseV2()
            return axios.get(`${base}/sales/sales-negotiations/${id}`)
                .then(res => {
                    const response = res.data


                    const index = this.negotiations.findIndex(n => n && n.id == id)
                    if (index !== -1) {
                        this.negotiations[index] = response
                    } else {
                        this.negotiations.push(response)
                    }

                    return response
                })
                .finally(() => {

                    this.loading.negotiation = false
                })
        },
        setSelectedNegotiationId(id) {
            this.selectedNegotiationId = id;
            if (id) {
                const exists = this.negotiations.find(n => n && n.id == id);
                if (!exists) {
                    return this.fetchNegotiation(id);
                }
            }
            return Promise.resolve(null);
        },
        fetchFunnels(preferredId = null) {
            const base = getUrlBaseV2()
            axios.get(`${base}/sales/sales-funnels`)
                .then(res => {
                    const response = res.data
                    this.funnels = response.data

                    // 1. If preferredId is provided, try to use it
                    if (preferredId) {
                        const preferred = this.funnels.find(f => f.id == preferredId)
                        if (preferred) {
                            this.currentFunnel = preferred
                            return
                        }
                    }

                    // 2. Preserve current funnel selection if it still exists in the list
                    if (this.currentFunnel) {
                        const stillExists = this.funnels.find(f => f.id === this.currentFunnel.id)
                        if (stillExists) {
                            this.currentFunnel = stillExists
                            return
                        }
                    }

                    // 3. Fallback to the first funnel
                    this.currentFunnel = response.data[0]
                })
        },
        fetchStages() {

        },
        async fetchNegotiations(payload = {}) {
            const page = payload.page || 1;

            if (page === 1) {
                this.loading.negotiations = true
                this.negotiations = []
                this.currentPage = 1
                this.lastPage = 1
            } else {
                this.loadingMore = true
            }
            const base = getUrlBaseV2()

            // Merge explicit payload with store filters
            const params = {
                sales_funnel_id: this.currentFunnel?.id,
                order_by: payload.order_by || 'created_at',
                order_direction: payload.order_direction || 'desc',
                limit: 200,
                page: page,
                ...this.filters,
                ...payload // payload can override store filters
            }

            // Re-sync store filters with the payload if provided
            if (payload.created_since !== undefined) this.filters.created_since = payload.created_since;
            if (payload.created_until !== undefined) this.filters.created_until = payload.created_until;

            let url = `${base}/sales/sales-negotiations?`;

            // Build query params
            const queryParts = [];
            for (const key in params) {
                if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
                    queryParts.push(`${key}=${encodeURIComponent(params[key])}`);
                }
            }
            url += queryParts.join('&');

            try {
                const res = await axios.get(url);
                const response = res.data;
                const newNegotiations = response.data || [];

                if (page === 1) {
                    this.negotiations = newNegotiations;
                } else {
                    const existingIds = new Set(this.negotiations.map(n => n.id));
                    const filteredNew = newNegotiations.filter(n => n && !existingIds.has(n.id));
                    this.negotiations.push(...filteredNew);
                }

                if (response.meta) {
                    this.currentPage = response.meta.current_page || page;
                    this.lastPage = response.meta.last_page || 1;
                }
                return response;
            } catch (error) {
                console.error("Erro ao buscar negociações:", error);
                throw error;
            } finally {
                if (page === 1) {
                    this.loading.negotiations = false
                } else {
                    this.loadingMore = false
                }
            }
        },

        storeFunnel(payload) {
            const base = getUrlBaseV2()
            return axios.post(`${base}/sales/sales-funnels`, payload)
                .then(res => {
                    this.currentFunnel = res.data;
                    return res.data
                })
        },
        storeStage(payload) {
            const base = getUrlBaseV2()
            return axios.post(`${base}/sales/sales-funnel-stages`, payload)
                .then(res => {
                    return res.data
                })
        },
        updateFunnel(id, payload) {
            const base = getUrlBaseV2()
            return axios.put(`${base}/sales/sales-funnels/${id}`, payload)
                .then(res => {
                    return res.data
                })
        },
        updateStage(id, payload) {
            const base = getUrlBaseV2()
            return axios.put(`${base}/sales/sales-funnel-stages/${id}`, payload)
                .then(res => {
                    return res.data
                })
        },
        storeNegotiation(payload) {
            const base = getUrlBaseV2()
            return axios.post(`${base}/sales/sales-negotiations`, payload)
                .then(res => {
                    return res.data
                })
        },
        destroyFunnel(id) {
            const base = getUrlBaseV2()
            return axios.delete(`${base}/sales/sales-funnels/${id}`)
                .then(res => {
                    this.funnels = this.funnels.filter(f => f.id !== id)

                    if (this.currentFunnel?.id === id) {
                        this.currentFunnel = this.funnels[0] || null
                    }
                    return res.data
                })
        },
        destroyStage(id) {
            const base = getUrlBaseV2()
            return axios.delete(`${base}/sales/sales-funnel-stages/${id}`)
                .then(res => {
                    if (this.currentFunnel && this.currentFunnel.stages) {
                        this.currentFunnel.stages = this.currentFunnel.stages.filter(s => s.id !== id)
                    }
                    return res.data
                })
        },
        fetchLostReasons(forceRefetch = false) {
            if (this.lostReasons && !forceRefetch) return Promise.resolve(this.lostReasons);
            if (this.lostReasonsPromise && !forceRefetch) return this.lostReasonsPromise;

            const base = getUrlBaseV2();
            this.lostReasonsPromise = axios.get(`${base}/sales/sales-lost-reasons`)
                .then(res => {
                    this.lostReasons = res.data?.data || res.data || res;
                    return this.lostReasons;
                })
                .finally(() => {
                    this.lostReasonsPromise = null;
                });
            return this.lostReasonsPromise;
        },
        storeLostReason(payload) {
            const base = getUrlBaseV2()
            return axios.post(`${base}/sales/sales-lost-reasons`, payload)
                .then(res => {
                    this.fetchLostReasons(true);
                    return res.data
                })
        },
        updateLostReason(id, payload) {
            const base = getUrlBaseV2()
            return axios.put(`${base}/sales/sales-lost-reasons/${id}`, payload)
                .then(res => {
                    this.fetchLostReasons(true);
                    return res.data
                })
        },
        destroyLostReason(id) {
            const base = getUrlBaseV2()
            return axios.delete(`${base}/sales/sales-lost-reasons/${id}`)
                .then(res => {
                    this.fetchLostReasons(true);
                    return res.data
                })
        },
        storeNegotiationTask(payload) {

            const base = getUrlBaseV2()
            return axios.post(`${base}/sales/sales-negotiation-tasks`, payload)
                .then(res => {
                    const response = res.data
                    const taskData = response.data || response
                    // Update the local negotiation's task list
                    const negIndex = this.negotiations.findIndex(n => n.id === payload.sales_negotiation_id)
                    if (negIndex !== -1 && taskData && taskData.id) {
                        if (!this.negotiations[negIndex].tasks) {
                            this.negotiations[negIndex].tasks = []
                        }
                        this.negotiations[negIndex].tasks.push(taskData)
                    }
                    return response
                })
        },
        destroyNegotiation(id) {
            const base = getUrlBaseV2()
            return axios.delete(`${base}/sales/sales-negotiations/${id}`)
                .then(res => {
                    this.negotiations = this.negotiations.filter(n => n.id !== id)
                    return res.data
                })
        },
        async updateNegotiation(id, payload) {
            const base = getUrlBaseV2()
            try {
                const res = await axios.put(`${base}/sales/sales-negotiations/${id}`, payload);
                const response = res.data;
                const index = this.negotiations.findIndex(n => n.id === id);
                if (index !== -1) {
                    this.negotiations[index] = { ...this.negotiations[index], ...response.data };
                }
                return response;
            } catch (error) {
                throw error;
            }
        },
        async updateNegotiationTask(id, payload) {
            const base = getUrlBaseV2()
            try {
                const res = await axios.put(`${base}/sales/sales-negotiation-tasks/${id}`, payload);
                const response = res.data;
                const taskData = response.data || response;
                // Update the local task in the negotiation's task list
                const negIndex = this.negotiations.findIndex(n => n.id === payload.sales_negotiation_id);
                if (negIndex !== -1 && this.negotiations[negIndex].tasks && taskData) {
                    const taskIndex = this.negotiations[negIndex].tasks.findIndex(t => t.id === id);
                    if (taskIndex !== -1) {
                        this.negotiations[negIndex].tasks[taskIndex] = { ...this.negotiations[negIndex].tasks[taskIndex], ...taskData };
                    }
                }
                return response;
            } catch (error) {
                throw error;
            }
        },
        async destroyNegotiationTask(id, negotiationId) {
            const base = getUrlBaseV2()
            try {
                const res = await axios.delete(`${base}/sales/sales-negotiation-tasks/${id}`);
                // Remove the task from the local negotiation's task list
                const negIndex = this.negotiations.findIndex(n => n.id === negotiationId);
                if (negIndex !== -1 && this.negotiations[negIndex].tasks) {
                    this.negotiations[negIndex].tasks = this.negotiations[negIndex].tasks.filter(t => t.id !== id);
                }
                return res.data;
            } catch (error) {
                throw error;
            }
        },
        async moveNegotiation(id, newStageId, extraData = {}) {
            const index = this.negotiations.findIndex(n => n.id === id);
            if (index === -1) return;

            const originalStageId = this.negotiations[index].sales_funnel_stage_id;
            const updatedNegotiation = { ...this.negotiations[index], sales_funnel_stage_id: newStageId, ...extraData };

            // Optimistic update
            this.negotiations[index] = updatedNegotiation;

            try {
                await this.updateNegotiation(id, updatedNegotiation);
            } catch (error) {
                // Rollback on error
                const rollbackIndex = this.negotiations.findIndex(n => n.id === id);
                if (rollbackIndex !== -1) {
                    this.negotiations[rollbackIndex] = { ...this.negotiations[rollbackIndex], sales_funnel_stage_id: originalStageId };
                }
                throw error;
            }
        },
        async newNegotiation(negotiation) {

            this.negotiations.push(negotiation);
        },
        setFunnels(funnels) {
            this.funnels = funnels;
        },
        setStages(stages) {
            this.stages = stages;
        },
        async transferNegotiations(payload) {
            const base = getUrlBaseV2()
            try {
                const res = await axios.post(`${base}/sales/sales-negotiations/transfer`, payload);
                return res.data;
            } catch (error) {
                throw error;
            }
        },
        fetchProducts(forceRefetch = false) {
            if (this.products && !forceRefetch) return Promise.resolve(this.products);
            if (this.productsPromise && !forceRefetch) return this.productsPromise;

            const base = getUrlBaseV2();
            this.productsPromise = axios.get(`${base}/sales/sales-service-products`)
                .then(res => {
                    this.products = res.data?.data || res.data || res;
                    return this.products;
                })
                .finally(() => {
                    this.productsPromise = null;
                });
            return this.productsPromise;
        },
        storeProduct(payload) {
            const base = getUrlBaseV2()
            return axios.post(`${base}/sales/sales-service-products`, payload)
                .then(res => {
                    this.fetchProducts(true);
                    return res.data
                })
        },
        updateProduct(id, payload) {
            const base = getUrlBaseV2()
            return axios.put(`${base}/sales/sales-service-products/${id}`, payload)
                .then(res => {
                    this.fetchProducts(true);
                    return res.data
                })
        },
        destroyProduct(id) {
            const base = getUrlBaseV2()
            return axios.delete(`${base}/sales/sales-service-products/${id}`)
                .then(res => {
                    this.fetchProducts(true);
                    return res.data
                })
        },
        associateProductToNegotiation(negotiationId, payload) {
            const base = getUrlBaseV2()
            return axios.post(`${base}/sales/sales-negotiations/${negotiationId}/service-products`, payload)
                .then(res => {
                    return res.data
                })
        },
        removeProductFromNegotiation(negotiationId, productId) {
            const base = getUrlBaseV2()
            return axios.delete(`${base}/sales/sales-negotiations/${negotiationId}/service-products/${productId}`)
                .then(res => {
                    return res.data
                })
        },
        fetchStatuses(forceRefetch = false) {
            if (this.statuses && !forceRefetch) return Promise.resolve(this.statuses);
            if (this.statusesPromise && !forceRefetch) return this.statusesPromise;

            const base = getUrlBaseV2();
            this.statusesPromise = axios.get(`${base}/sales/sales-negotiation-statuses`)
                .then(res => {
                    this.statuses = res.data?.data || res.data || res;
                    return this.statuses;
                })
                .finally(() => {
                    this.statusesPromise = null;
                });
            return this.statusesPromise;
        },
        storeStatus(payload) {
            const base = getUrlBaseV2()
            return axios.post(`${base}/sales/sales-negotiation-statuses`, payload)
                .then(res => {
                    this.fetchStatuses(true); // invalidates cache
                    return res.data
                })
        },
        updateStatus(id, payload) {
            const base = getUrlBaseV2()
            return axios.put(`${base}/sales/sales-negotiation-statuses/${id}`, payload)
                .then(res => {
                    this.fetchStatuses(true); // invalidates cache
                    return res.data
                })
        },
        destroyStatus(id) {
            const base = getUrlBaseV2()
            return axios.delete(`${base}/sales/sales-negotiation-statuses/${id}`)
                .then(res => {
                    this.fetchStatuses(true); // invalidates cache
                    return res.data
                })
        },
        getAutomationMeta() {
            const base = getUrlBaseV2()
            return axios.get(`${base}/sales/negotiation/automations/meta`)
                .then(res => {
                    return res.data
                })
        },

        getAutomationsByFunnel(id) {
            const base = getUrlBaseV2()
            return axios.get(`${base}/sales/negotiation/automations?funnel_id=${id}`)
                .then(res => {
                    return res.data
                })
        },
        storeAutomation(payload) {
            const base = getUrlBaseV2()
            return axios.post(`${base}/sales/negotiation/${payload.sales_funnel_id}/automations`, payload)
                .then(res => res.data)
        },
        updateAutomation(id, payload) {
            const base = getUrlBaseV2()
            return axios.put(`${base}/sales/negotiation/automations/${id}`, payload)
                .then(res => res.data)
        },
        destroyAutomation(id) {
            const base = getUrlBaseV2()
            return axios.delete(`${base}/sales/negotiation/automations/${id}`)
                .then(res => res.data)
        },
        activateAutomation(id) {
            const base = getUrlBaseV2()
            return axios.patch(`${base}/sales/negotiation/automations/${id}/activate`)
                .then(res => res.data)
        },
        deactivateAutomation(id) {
            const base = getUrlBaseV2()
            return axios.patch(`${base}/sales/negotiation/automations/${id}/deactivate`)
                .then(res => res.data)
        }
    }
})